<template>
  <modal-wrapper
    ref="modalRef"
    :display-cancel-button="false"
    :header="
      t('documentUpload.metadataFor', {
        fileName: metadata.documentId,
      })
    "
    :ok-button-label="t('generic.close')"
    :size="mode === 'comparison' ? ModalSize.XL : ModalSize.L"
  >
    <table-wrapper
      v-model:page-options="pageOptions"
      :fields="tableFields as any"
      :filter-display="false"
      :items="transformedMetadata"
      :paginator="false"
      :row-class="rowColors"
    >
      <template #cell(field)="{ data }">
        <div
          v-if="data.field === 'Filepath'"
          class="flex gap-2 items-center"
        >
          {{ t(`contentTypeFields.${data.field}`) }}
          <div
            v-tooltip.right="{
              value: t('documentDetailView.filepathInfo'),
              pt: {
                text: 'w-[20rem]',
              },
            }"
          >
            <font-awesome-icon :icon="['fas', 'circle-info']" />
          </div>
        </div>
        <div v-else>
          {{ t(`contentTypeFields.${data.field}`) }}
        </div>
      </template>
      <template #cell(oldValue)="{ data }">
        <!-- The old value is returned from the backend only if it differs from the -->
        <!-- new one. If not, only the new value is returned. -->
        {{ data.oldValue || data.newValue }}
      </template>
    </table-wrapper>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ModalSize } from '@/components/ModalWrapper/ModalWrapper.types';
  import {
    RowClass,
    TableFieldsBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { useModalWrapper } from '../ModalWrapper';
  import { MetadataModalMode } from '../MassUploadTable/MassUploadTable.types';
  import { IMetadataDifferences } from 'types';

  type TransformedMetadata =
    | ITransformedMetadata[]
    | ITransformedMetadataComparison[];

  interface ITransformedMetadata {
    field: string;
    value: number;
  }

  interface ITransformedMetadataComparison {
    field: string;
    newValue: string;
    oldValue: string;
  }

  const { metadata = {}, ...props } = defineProps<{
    metadata?: Record<string, any>;
    mode?: MetadataModalMode;
    metadataChanges?: IMetadataDifferences;
  }>();

  const { t } = useI18n();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const transformedMetadata = ref<TransformedMetadata[]>([]);

  const { pageOptions } = useTableWrapper<
    ITransformedMetadata | ITransformedMetadataComparison
  >(TableMode.CLIENT_SIDE, 'field', undefined, undefined, false);
  const tableFields = computed(() => {
    if (props.mode === MetadataModalMode.COMPARISON) {
      return new TableFieldsBuilder<ITransformedMetadataComparison>({
        labelTranslationKey: 'documentUpload',
        sortable: false,
      })
        .addField('field', { width: '26%' })
        .addField('oldValue', { width: '37%' })
        .addField('newValue', { width: '37%' })
        .build();
    } else {
      return new TableFieldsBuilder<ITransformedMetadata>({
        labelTranslationKey: 'documentUpload',
        sortable: true,
      })
        .addField('field', { width: '30%' })
        .addField('value', { width: '70%' })
        .build();
    }
  });

  function rowColors(item: ITransformedMetadataComparison) {
    if (
      props.mode === MetadataModalMode.COMPARISON &&
      props.metadataChanges &&
      typeof props.metadataChanges !== 'string' &&
      props.metadataChanges[item.field]
    ) {
      return RowClass.SELECTED;
    }
  }

  watch(
    () => metadata,
    (newMetadata) => {
      if (
        props.mode === MetadataModalMode.COMPARISON &&
        props.metadataChanges
      ) {
        const updatedMetadata: ITransformedMetadataComparison[] = [];

        for (const field in newMetadata) {
          if (props.metadataChanges[field]) {
            updatedMetadata.push({
              field,
              oldValue: props.metadataChanges[field].previous,
              newValue: newMetadata[field],
            });

            continue;
          }

          updatedMetadata.push({
            field,
            oldValue: '',
            newValue: newMetadata[field],
          });
        }

        transformedMetadata.value = updatedMetadata as any;
      }

      if (!props.mode || props.mode === 'normal') {
        const updatedMetadata: ITransformedMetadata[] = [];

        for (const field in newMetadata) {
          updatedMetadata.push({
            field,
            value: newMetadata[field],
          });
        }

        transformedMetadata.value = updatedMetadata as any;
      }
    },
    { deep: true },
  );
</script>

<style scoped lang="scss">
  .metadata-list {
    li {
      margin: 10px 0;
    }
  }
</style>
